<template>
  <div :class="['toolbar']" v-if="editMode">
    <input type="color" class="color-input" :value="textColor" @input="updateTextColor" />

    <button @click="toggleBold" class="bold" :class="{ active: editor.isActive('bold') }">B</button>
    <button @click="toggleItalic" class="italic" :class="{ active: editor.isActive('italic') }">I</button>
    <button @click="toggleStrike" class="line-through" :class="{ active: editor.isActive('strike') }">S</button>
    <button @click="toggleUnderline" class="underline" :class="{ active: editor.isActive('underline') }">U</button>
    <button @click="toggleCode" class="code" :class="{ active: editor.isActive('code') }">{}</button>

    <button @click="toggleBulletList" class="orderedList" :class="{ active: editor.isActive('bulletList') }">
      <span>• –––</span>
      <span>• –––</span>
      <span>• –––</span>
    </button>
    <button @click="toggleOrderedList" class="orderedList" :class="{ active: editor.isActive('orderedList') }">
      <span>1 –––</span>
      <span>2 –––</span>
      <span>3 –––</span>
    </button>

    <button @click="addOrEditLink" :class="{ active: editor.isActive('link') }">🌐</button>






    <button @click="toggleHeading(2)" class="ml-20 heading fw-black" :class="{ active: editor.isActive('heading', { level: 2 }) }">H2</button>
    <button @click="toggleHeading(3)" class="heading fw-bold" :class="{ active: editor.isActive('heading', { level: 3 }) }">H3</button>
    <button @click="toggleHeading(4)" class="heading fw-semibold" :class="{ active: editor.isActive('heading', { level: 4 }) }">H4</button>

    <button @click="editor.chain().focus().setHorizontalRule().run()"
            class="divider-button"
            title="Add horizontal line">
      <span class="horizontal-line-icon">—</span>
    </button>

    <button @click="setTextAlign('left')" class="ml-20 text-align align-items-start" :class="{ active: editor.isActive({ textAlign: 'left' }) }">
      <span>––</span>
      <span>–––</span>
      <span>––</span>
    </button>

    <button @click="setTextAlign('center')" class="text-align" :class="{ active: editor.isActive({ textAlign: 'center' }) }">
      <span>––</span>
      <span>–––</span>
      <span>––</span>
    </button>

    <button @click="setTextAlign('right')" class="text-align align-items-end" :class="{ active: editor.isActive({ textAlign: 'right' }) }">
      <span>––</span>
      <span>–––</span>
      <span>––</span>
    </button>

    <button @click="setTextAlign('justify')" class="text-align " :class="{ active: editor.isActive({ textAlign: 'justify' }) }">
      <span>–––</span>
      <span>–––</span>
      <span>–––</span>
    </button>

    <button @click="toggleFullWidthHeader"  class="fullWidthHeader" :class="{ active: editor.isActive('fullWidthHeader') }">H</button>
    <input type="color" class="fullWidthHeader-color" :value="fullWidthHeaderColor" @input="updateFullWidthHeaderColor" />


    <Loader v-if="isLoading" visible />

    <Notification
        v-if="notificationMessage"
        :message="notificationMessage"
        :type="notificationType"
        :duration="3000"
        @closed="notificationMessage = null"
    />

    <input
        type="file"
        @change="uploadImage"
        class="image-upload"
        accept="image/*"
        style="display: none;"
        id="image-upload"
    />
    <label for="image-upload" class="ml-20 image-upload" :class="{ 'disabled': isLoading }">
        {{ isLoading ? '⌛' : '📷' }}
    </label>


<!--    <button @click="insertCopyText" class="copy-text-button">📋</button>-->
    <button @click="toggleCopyText"  class="ml-20" :class="{ active: editor.isActive('copyText') }">📋 <></button>



    <button @click="toggleCodeJava" class="ml-20" :class="{ active: editor.isActive('codeBlockJava') }">Java</button>
    <button @click="toggleCodeBash" class="" :class="{ active: editor.isActive('codeBlockBash') }">Bash</button>

  </div>
</template>

<script>
import Loader from "../../Loader.vue";
import Notification from "../../Notification.vue";
import { s3Service } from "../../../../services/s3Service.js";

export default {
  components: {
    Loader,
    Notification
  },
  props: {
    editor: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    fullWidthHeaderColor: {
      type: String,
      default: "#e0e0e5"
    },
    copyTextColor: {
      type: String,
      default: "#e0e0e5"
    },
    textColor: {
      type: String,
      default: "#000000"
    },
    folderName: {
      type: String,
      default: "tutorials"
    }
  },
  data() {
    return {
      isToolbarFixed: false,
      isLoading: false,
      notificationMessage: null,
      notificationType: "info"
    };
  },
  methods: {
    toggleCode() {
      this.editor.chain().focus().toggleCode().run();
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    toggleCodeJava() {
      if (this.editor.isActive('codeBlockJava')) {
        this.editor.chain().focus().setNode('paragraph').run();
      } else {
        this.editor.chain().focus().toggleCodeBlock({ language: 'java' }).run();
      }
    },
    toggleCodeBash() {
      if (this.editor.isActive('codeBlockJava')) {
        this.editor.chain().focus().setNode('paragraph').run();
      } else {
        this.editor.chain().focus().toggleCodeBlock({ language: 'bash' }).run();
      }
    },
    toggleFullWidthHeader() {
      this.editor.chain().focus().toggleNode("fullWidthHeader", "paragraph", {
        backgroundColor: this.fullWidthHeaderColor
      }).run();
    },
    updateFullWidthHeaderColor(event) {
      this.$emit('update:fullWidthHeaderColor', event.target.value);
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run();
    },
    toggleStrike() {
      this.editor.chain().focus().toggleStrike().run();
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run();
    },
    addOrEditLink() {
      const previousUrl = this.editor.getAttributes('link').href;
      const url = prompt('Enter URL', previousUrl || 'https://');

      if (url === null) {
        return;
      }

      if (url === '') {
        this.editor.chain().focus().unsetLink().run();
      } else {
        this.editor.chain().focus().setLink({ href: url }).run();
      }
    },
    toggleHeading(level) {
      this.editor.chain().focus().toggleHeading({ level }).run();
    },
    toggleCopyText() {
      this.editor.chain().focus().toggleNode("copyText", "paragraph", {
        backgroundColor: this.copyTextColor
      }).run();
    },
    updateTextColor(event) {
      this.editor.chain().focus().setColor(this.textColor).run();
      this.$emit('update:textColor', event.target.value);
    },
    setTextAlign(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },
    showNotification(message, type = "info") {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    async uploadImage(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isLoading = true;
      try {
        const optimizedFile = await this.optimizeImage(file);

        const imageUrl = await s3Service.uploadFile(
          optimizedFile,
          `${this.folderName}`
        );

        this.editor.chain().focus().setImage({
          src: imageUrl,
          loading: 'lazy'
        }).run();

        this.showNotification("Image uploaded successfully!", "success");
      } catch (error) {
        console.error('Error uploading image:', error);
        this.showNotification(error.message || "Failed to upload image", "error");
      } finally {
        this.isLoading = false;
      }
    },
    async optimizeImage(file) {
      if (!file.type.startsWith('image/')) return file;

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 1080;

            let width = img.width;
            let height = img.height;

            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }

            canvas.width = width;
            canvas.height = height;

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.85);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  handleScroll() {
    const toolbarTop = this.$el.offsetTop;
    this.isToolbarFixed = window.scrollY > toolbarTop;
  }
};
</script>

<style scoped>

.toolbar button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: none;
  padding: 0 10px;
  color: var(--my-dark);
  background-color: var(--my-white);
  cursor: pointer;
  border-radius: 5px;
  font-size: 20px !important;
  font-weight: 500;
  transition: background-color 0.3s;
}
.toolbar button:hover {
  background-color: var(--my-links);
}
.toolbar button.active {
  background-color: var(--my-links);
  color: var(--my-light);
}
.bold {
  font-weight: 800 !important;
}

.italic {
  font-style: italic;
}

.line-through {
  text-decoration: line-through;
}

.underline {
  text-decoration: underline;
}

.code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px !important;
  font-weight: 500;
}

button.code {
  padding: 0 12px;
}

button.orderedList {
  display: flex;
  font-size: 7px !important;
  line-height: 6px;
  justify-content: center;
  font-weight: 900;
  align-items: center;
}

button.text-align {
  display: flex;
  font-size: 12px !important;
  line-height: 6px;
  justify-content: center;
  font-weight: 900;
  align-items: center;
}

button.heading {
  font-size: 12px !important;
}


.image-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 26px;
  background-color: var(--my-white);
  border-radius: 5px;
  padding-top: 2px;
}

.image-upload:hover {
  background-color: var(--my-links);
  cursor: pointer;
  transition: background-color 0.2s ease;
}


.toolbar {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  height: 60px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #f5f5f5;
  padding: 12px 120px 12px 10px;
  z-index: 1200;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}
.toolbar > * {
  flex-shrink: 0;
  min-width: 36px;
}
.toolbar::-webkit-scrollbar {
  display: none;
}



.toolbar input.color-input {
  padding: 0;
  height: 36px;
  width: 36px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
}

.toolbar input.color-input:hover {
 opacity: 90%;
}

.fullWidthHeader {
  border-radius: 5px 0 0 5px !important;
}

input.fullWidthHeader-color {
  margin-left: -10px;
  padding: 0;
  outline: none;
  border: none;
  border-radius: 0 5px 5px 0 !important;
  cursor: pointer;
  height: 36px;
  width: 36px;
}

.fullWidthHeader-color:hover {
  opacity: 90%;
}

@media (max-width: 900px) {
  .image-upload {
    padding-top: 0;
  }
}

.disabled {
    opacity: 0.5;
    cursor: not-allowed !important;
    pointer-events: none;
}

.image-upload.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.horizontal-line-icon {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.divider-button {
  margin-left: 10px;
}

.divider-button:hover {
  background-color: var(--my-links);
  color: var(--my-light);
}

</style>
